// Frontend models for the Scheduling/Booking module. Booking equals the backend BookingDTO.

export enum BookingStatus {
  OPPORTUNITY = 'opportunity',
  REQUESTED = 'requested',
  AWAITING_REPLY = 'awaiting_reply',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
}

export enum MeetingMode {
  VIRTUAL = 'virtual',
  IN_PERSON = 'in_person',
}

export enum LocationKind {
  FIXED = 'fixed',
  ACCOUNT_DEFAULT = 'account_default',
  USER_DEFAULT = 'user_default',
}

export interface LocationRef {
  kind: LocationKind
  id?: string | null
}

export enum DeliveryMode {
  SEND = 'send',
  DRAFT = 'draft',
}

export enum ResourceRefType {
  ROOM = 'room',
  SESSION = 'session',
  EMAIL = 'email',
}

export interface ResourceRef {
  type: ResourceRefType
  ids: Record<string, string>
}

export interface ClientConsent {
  text: string
  status?: 'accepted' | null
  consentedAt?: string | null
}

export interface BookingClient {
  contactId?: string
  firstName?: string
  lastName?: string
  email?: string
  timeZone?: string
  inviteUrl?: string
  unsubscribeUrl?: string
  unsubscribedAt?: string | null
  consent?: ClientConsent | null
}

export interface UnsubscribedClient {
  email: string
  unsubscribedAt: string
}

export interface BookingInvitee {
  userId?: string
  contactId?: string
  firstName?: string
  lastName?: string
  email: string
  role?: string
}

export enum CalendarResponseStatus {
  NEEDS_ACTION = 'needs_action',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
}

export interface BookingCoHost {
  userId: string
  firstName: string | null
  lastName: string | null
  required: boolean
  calendarResponse: CalendarResponseStatus | null
}

export interface ReminderInfo {
  automated?: boolean
  count?: number
  total?: number
  lastSentAt?: string
  nextSendAt?: string
}

export enum BookingActor {
  USER = 'user',
  CLIENT = 'client',
}

export interface ActorRef {
  actor: BookingActor
  id: string | null
}

export interface Booking {
  bookingId: string
  status: BookingStatus
  title?: string
  eventTypeId?: string
  userId: string
  clients: BookingClient[]
  additionalInvitees?: BookingInvitee[]
  coHosts?: BookingCoHost[]
  bookedBy?: BookingClient
  source?: ResourceRef
  start?: string
  end?: string
  suggestedDate?: string
  invitationSent?: boolean
  invitationSentAt?: string | null
  reminders?: ReminderInfo
  rescheduledBy?: string
  originalStart?: string
  clientMessage?: string
  rescheduleMessage?: string
  room?: ResourceRef
  session?: ResourceRef
  meetingMode?: MeetingMode
  location?: LocationRef | null
  meetingUrl?: string
  manageUrl?: string
  rescheduleUrl?: string
  cancelUrl?: string
  cancellationReason?: string | null
  closedAt?: string | null
  closedBy?: ActorRef | null
  createdAt: string
  updatedAt: string
}

export enum ArchiveReason {
  CANCELLED = 'cancelled',
  DECLINED_BOOKING_REQUEST = 'declined_booking_request',
  DECLINED_RESCHEDULE_REQUEST = 'declined_reschedule_request',
  DISMISSED = 'dismissed',
  ARCHIVED = 'archived',
  MEETING_TIME_PASSED = 'meeting_time_passed',
  EXPIRED_BOOKING_REQUEST = 'expired_booking_request',
  EXPIRED_RESCHEDULE_REQUEST = 'expired_reschedule_request',
  EXPIRED_BOOKING_LINK = 'expired_booking_link',
}

// Expired rows keep their live status (requested / awaiting_reply / confirmed) — always label rows by archiveReason, never by status.
export interface ArchivedBooking extends Booking {
  archiveReason: ArchiveReason
  archivedAt: string
}

export enum EventTypeAccess {
  ACCOUNT = 'account',
  PERSONAL = 'personal',
}

export enum DayOfWeek {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export interface TimeInterval {
  start: string
  end: string
}

export interface DayAvailability {
  day: DayOfWeek
  intervals: TimeInterval[]
}

export interface AvailabilitySchedule {
  days: DayAvailability[]
}

export interface SchedulingSlot {
  start: string
  end: string
  userId?: string
}

export interface Location {
  id: string
  name: string
  address: string
  note?: string
  timeZone?: string
  ownerUserId?: string
  updatedAt: string
}

export enum AvailabilityMode {
  ACCOUNT_DEFAULT = 'account_default',
  USER_DEFAULT = 'user_default',
  CUSTOM = 'custom',
}

export enum EmailTemplatePurpose {
  INVITATION_TO_BOOK = 'invitation_to_book',
  BOOKING_REQUEST_DECLINED = 'booking_request_declined',
  INVITATION_TO_REBOOK = 'invitation_to_rebook',
  RESCHEDULE_REQUEST_DECLINED = 'reschedule_request_declined',
  CHANGE_NOTICE = 'change_notice',
  CANCELLATION_WITH_MESSAGE = 'cancellation_with_message',
  REMINDER_TO_BOOK = 'reminder_to_book',
  MEETING_REMINDER = 'meeting_reminder',
  REQUEST_AWAITING_APPROVAL = 'request_awaiting_approval',
  NEW_BOOKING_NOTIFICATION = 'new_booking_notification',
  RESCHEDULING_NOTIFICATION = 'rescheduling_notification',
  CANCELLATION_NOTIFICATION = 'cancellation_notification',
}

export enum SchedulingEmailAudience {
  CLIENTS = 'Sent to clients',
  ADVISOR = 'Sent to team members',
}

export enum SchedulingEmailSlug {
  INVITATION_TO_BOOK = 'cl_book_invite',
  BOOKING_REQUEST_DECLINED = 'cl_book_decl',
  INVITATION_TO_REBOOK = 'cl_rebook_invite',
  RESCHEDULE_REQUEST_DECLINED = 'cl_resched_decl',
  CHANGE_NOTICE = 'cl_change_notice',
  CANCELLATION_WITH_MESSAGE = 'cl_cancel_msg',
  REMINDER_TO_BOOK = 'cl_book_reminder',
  MEETING_REMINDER = 'cl_meeting_rem',
  REQUEST_AWAITING_APPROVAL = 'ad_req_pending',
  NEW_BOOKING_NOTIFICATION = 'ad_new_booking',
  RESCHEDULING_NOTIFICATION = 'ad_resched',
  CANCELLATION_NOTIFICATION = 'ad_cancel_notify',
}

export interface EmailTemplate {
  id: string
  name: string
  purpose: EmailTemplatePurpose
  subject: string
  body: string
}

export enum SmsTemplatePurpose {
  MEETING_REMINDER = 'meeting_reminder',
  CANCELLATION_NOTICE = 'cancellation_notice',
}

export interface SmsTemplate {
  name: string
  purpose: SmsTemplatePurpose
  body: string
}

export enum SmsNumberStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
}

export interface SmsNumber {
  id: string
  phone: string
  areaCode: string
  status: SmsNumberStatus
  isDefault: boolean
}

export interface SmsAreaCode {
  areaCode: string
  city: string | null
  state: string
  stateName: string
}

export interface RedtailEventConfig {
  activityCodeId: number
  categoryId: number
}

export interface EventConfig {
  redtail?: Partial<Record<string, RedtailEventConfig>>
}

export interface EventConfigInput {
  redtail: RedtailEventConfig | null
}

export interface SchedulingEventType {
  id: string
  slug: string
  name: string
  description?: string
  durationMinutes: number
  clientFacingText?: string
  connectedMeetingTypeId?: string
  active: boolean
  access: EventTypeAccess
  addToUserPagesByDefault: boolean
  meetingMode: MeetingMode
  location?: LocationRef | null
  availabilityMode: AvailabilityMode
  availabilitySchedule?: AvailabilitySchedule
  bufferBeforeMinutes?: number
  bufferAfterMinutes?: number
  minimumNoticeMinutes?: number
  bookingWindowDays?: number
  hostMode?: HostMode
  accountPageVisible?: boolean
  hostSummary?: HostSummary
  myCoHostScopes?: CoHostScope[]
  usedByCount: number
  updatedAt: string
  eventConfig?: EventConfig | null
}

export enum HostMode {
  SINGLE = 'single',
  ROTATING = 'rotating',
}

export interface HostSummary {
  hostCount: number
  eligibleHostCount: number
  singleHostName: string | null
}

export interface EventTypeHost {
  userId: string
  name?: string | null
  email?: string | null
  roleIds: string[]
  status: string | null
  eligible: boolean
  calendar: CalendarIntegration | null
  lastAssignedAt: string | null
}

export interface EventTypeHosts {
  hostMode: HostMode
  hosts: EventTypeHost[]
}

export enum CoHostScope {
  ACCOUNT = 'account',
  INDIVIDUAL = 'individual',
}

export interface EventTypeCoHost {
  userId: string
  name?: string | null
  email?: string | null
  status: string | null
  eligible: boolean
  calendar: CalendarIntegration | null
  required: boolean
}

export interface EventTypeCoHosts {
  account: EventTypeCoHost[]
  individual: EventTypeCoHost[]
}

export interface CoHostInput {
  userId: string
  required: boolean
}

export interface SchedulingLink {
  id: string
  expiresAt?: string | null
}

export interface SchedulingUser {
  id: string
  slug?: string
  name?: string
  email?: string
  timeZone?: string
  avatarUrl?: string
  publicUrl?: string
}

export enum ApprovalMode {
  ALWAYS_REQUIRE = 'always',
  NEVER_REQUIRE = 'never',
  USERS_DECIDE = 'users_decide',
}

export enum AutomaticInvitesMode {
  OFF = 'off',
  ON = 'on',
  USERS_DECIDE = 'users_decide',
}

export interface SchedulingBranding {
  brandColor: string
}

export interface PublicBookingPage {
  accountSlug: string | null
  branding: SchedulingBranding
  showProfilePhotos: boolean
  showAccountName: boolean
  showLogo: boolean
  accountBookingEnabled?: boolean
}

export interface UserControls {
  allowManageOwnLocations: boolean
}

export interface ApprovalSettings {
  booking: ApprovalMode
  reschedule: ApprovalMode
}

export interface Automations {
  automaticInvites: AutomaticInvitesMode
}

export interface AccountDefaults {
  locationId: string | null
  availabilitySchedule: AvailabilitySchedule
  excludeHolidays: boolean
}

export interface ConsentSettings {
  enabled: boolean
  text?: string | null
}

export interface Compliance {
  disclosureText: string
  consent?: ConsentSettings
}

export interface SmsNotificationSettings {
  enabled: boolean
  reminders: boolean
  cancellations: boolean
  enterpriseEnforced?: boolean
}

export interface NotificationSettings {
  sms: SmsNotificationSettings
}

export interface AccountSchedulingSettings {
  publicBookingPage: PublicBookingPage
  userControls: UserControls
  approval: ApprovalSettings
  automations: Automations
  defaults: AccountDefaults
  compliance: Compliance
  notifications: NotificationSettings
}

export enum ReminderType {
  FOLLOW_UP = 'follow_up',
  MEETING = 'meeting',
}

export enum NotificationChannel {
  EMAIL = 'email',
  SMS = 'sms',
}

export interface ReminderEntry {
  id: string | null
  offsetMinutes: number
  channels: NotificationChannel[]
}

export interface ReminderSettings {
  reminderType: ReminderType
  enabled: boolean
  entries: ReminderEntry[]
  allowedOffsets: number[]
}

export interface ReminderSettingsResponse {
  data: ReminderSettings[]
}

export interface ReminderEntryInput {
  id?: string
  offsetMinutes: number
  channels: NotificationChannel[]
}

export enum PastDuePolicy {
  SEND = 'send',
  SKIP = 'skip',
}

export interface ReminderSettingsUpdate {
  enabled: boolean
  entries: ReminderEntryInput[]
  pastDuePolicy: PastDuePolicy
}

export interface ReminderSettingsPreview {
  pastDueCount: number
}

export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
  EXCHANGE = 'exchange',
  REDTAIL = 'redtail',
}

export enum MeetingPlatformProvider {
  ZOOM = 'zoom',
  GOOGLE_MEET = 'google_meet',
  MS_TEAMS = 'ms_teams',
}

export interface UserBookingPage {
  slug: string | null
  showProfilePhoto: boolean
}

export enum SchedulingIntegrationState {
  LIVE = 'live',
  INVALID = 'invalid',
  VALIDATING = 'validating',
}

export interface CalendarIntegration {
  provider: CalendarProvider
  connected: boolean
  state?: SchedulingIntegrationState
  capable: boolean
}

export interface MeetingPlatformIntegration {
  provider: MeetingPlatformProvider
  connected: boolean
  state?: SchedulingIntegrationState
  capable: boolean
}

export interface UserIntegrations {
  calendars: CalendarIntegration[]
  meetingPlatforms: MeetingPlatformIntegration[]
  selectedCalendar: CalendarProvider | null
  selectedMeetingPlatform: MeetingPlatformProvider | null
}

export interface UserDefaults {
  locationId: string | null
  availabilitySchedule: AvailabilitySchedule | null
  excludeHolidays: boolean | null
}

export type UserApprovalMode = Exclude<ApprovalMode, ApprovalMode.USERS_DECIDE>

export type UserAutomaticInvitesMode = Exclude<AutomaticInvitesMode, AutomaticInvitesMode.USERS_DECIDE>

export interface UserApproval {
  booking: UserApprovalMode
  reschedule: UserApprovalMode
}

export interface UserAutomations {
  automaticInvites: UserAutomaticInvitesMode
}

export interface UserBookingRules {
  approval: UserApproval
  automations: UserAutomations
  publicBookingLinksEnabled: boolean
}

export interface UserSchedulingSettings {
  bookingPage: UserBookingPage
  integrations: UserIntegrations
  defaults: UserDefaults
  bookingRules: UserBookingRules
}

export type SettingsResetScope = 'availability' | 'booking_rules'

export interface SchedulingFilters {
  status?: BookingStatus
  userIds?: string[]
  contactIds?: string[]
  eventTypeIds?: string[]
  search?: string
  dateFrom?: Date
  dateTo?: Date
}
